import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { MdClose, MdModeEditOutline, MdSave } from "react-icons/md";
import { Grid, Loader, Table } from "semantic-ui-react";

import { useGetFacilityWholeList } from "../../../../api/cultivations";
import { useUpdateExternalStorage } from "../../../../api/general/storage";
import { DropDown, InputText, TitleView } from "../../../../components";
import { InputNumber } from "../../../../components/InputText/InputNumber";
import InputWithDropDown from "../../../../components/inputWithDropDown/InputWithDropDown";
import {
  dataTypeOptions,
  storageEnvTypeOptions,
  WEIGHT_UNIT_TYPES_OPTIONs
} from "../../../../config/constants";
import { FacilityType } from "../../../../config/enums/FacilityType.enum";
import { IngredientDataTypesOption } from "../../../../config/enums/IngredientType.enum";
import { MEASUREMENT_TYPES } from "../../../../config/enums/MeasurementTypes";
import { StorageEnvType } from "../../../../config/enums/StorageType.enum";
import { checkValueIsEmpty } from "../../../../utils/utils";

import "./ExternalStorage.scss";

const ExternalWarehouse = ({ externalStorage, refetch, isEditable }: any) => {
  const [selectStorage, setSelectStorage] = useState<any>(null);
  const [dataType, setDataType] = useState<string | undefined>(IngredientDataTypesOption.SPECIFIC);
  const [storageEnvType, setStorageEnvType] = useState<number | undefined>(StorageEnvType.AMBIENT);
  const [weightUnitValue, setWeightUnit] = useState(MEASUREMENT_TYPES.WEIGHT_KG);
  const [warehouse, setWarehouse] = useState<string | undefined>();

  const { data: facilityData } = useGetFacilityWholeList(FacilityType.STORAGE);

  const { mutate: externalStorageUpdateDataMutate, isLoading: isExternalStorageUpdateLoading } =
    useUpdateExternalStorage();

  const facilities = useMemo(() => {
    const list = facilityData || [];
    return [...list];
  }, [facilityData]);

  const externalStorageData = useMemo(() => {
    return externalStorage || [];
  }, [externalStorage]);

  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
    reset,
    setValue
  } = useForm<any>({
    defaultValues: {
      externalStorageData
    }
  });

  const editData = (row: any) => {
    setSelectStorage(row);
    setDataType(row?.dataType);
    setStorageEnvType(row?.storageEnv);
    setWarehouse(row?.facilityId?._id);
    reset({
      externalStorageData: externalStorageData.map((gate: any) => ({
        ...gate
      }))
    });
  };
  const handleCancel = () => {
    setSelectStorage(null);
    reset();
  };

  const onSubmit = (data: any) => {
    const currentUpdateData = data?.externalStorageData?.find(
      (storage: any) => storage._id === selectStorage._id
    );
    if (currentUpdateData) {
      const { _id, tenantId, ...payloadWithoutId } = currentUpdateData;

      const updateExternalStorage = {
        ...payloadWithoutId,
        externalStorageId: selectStorage._id,
        weightUnit: weightUnitValue
      };
      externalStorageUpdateDataMutate(updateExternalStorage, {
        onSuccess: () => {
          setSelectStorage(null);
          refetch();
        }
      });
    }
  };

  return (
    <>
      <TitleView CustomTitleViewMain="mt-2" title="External Warehouse" />
      <Table celled className="table" structured>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell className="tableHeader" colSpan="1" textAlign="center">
              External Warehouse
            </Table.HeaderCell>
            <Table.HeaderCell
              className="tableHeader"
              colSpan="15"
              textAlign="center"
              style={{ backgroundColor: "#f4c6db", color: "#000" }}
            >
              Input field
            </Table.HeaderCell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell className="tableHeaderReview">External Storage Name</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Data Type</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Storage Environment</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Warehouse Name</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">
              Storage Temperature (℃)
            </Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Warehouse size (m3)</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Weight</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Electricity (kWh)</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">
              Utilization of warehouse %
            </Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Storage day(s)</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview"></Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body className="customStorageDropDown">
          {externalStorage.map((data: any, index: any) => {
            const storageEnv = storageEnvTypeOptions.find((e: any) => e.key === data?.storageEnv);
            const weightUnit = WEIGHT_UNIT_TYPES_OPTIONs.find(
              (e: any) => e.key === data?.weightUnit
            );
            const isSelectStorage = selectStorage?._id === data?._id;

            return (
              <Table.Row>
                <Table.Cell className={checkValueIsEmpty(data?.externalStorageName)}>
                  {isSelectStorage ? (
                    <InputText
                      register={register}
                      errors={errors.externalStorageName}
                      placeholder="External Storage Name"
                      name={`externalStorageData.${index}.externalStorageName`}
                      required={true}
                      errorMessage={"Please enter External Storage Name"}
                    />
                  ) : (
                    <> {data?.externalStorageName || "Missing storage name"}</>
                  )}
                </Table.Cell>
                <Table.Cell className={checkValueIsEmpty(data?.dataType)}>
                  {isSelectStorage ? (
                    <DropDown
                      required={true}
                      placeholder="Select Storage Type"
                      upward={true}
                      currentData={dataTypeOptions}
                      defaultValue={dataType}
                      customGridColumn={"customGridColumnType"}
                      handleChangeState={(e: any, { value }: any) => {
                        setDataType(value);
                        setValue(`externalStorageData.${index}.dataType`, value, {
                          shouldDirty: true,
                          shouldValidate: true
                        });
                      }}
                    />
                  ) : (
                    <>{data?.dataType || "Missing data type"}</>
                  )}
                </Table.Cell>
                <Table.Cell className={checkValueIsEmpty(storageEnv?.text)}>
                  {isSelectStorage ? (
                    <DropDown
                      required={true}
                      placeholder="Select Storage Environment"
                      upward={true}
                      currentData={storageEnvTypeOptions}
                      defaultValue={storageEnvType}
                      customGridColumn={"customGridColumnType"}
                      handleChangeState={(e: any, { value }: any) => {
                        setStorageEnvType(value);
                        setValue(`externalStorageData.${index}.storageEnv`, value, {
                          shouldDirty: true,
                          shouldValidate: true
                        });
                      }}
                    />
                  ) : (
                    <> {storageEnv?.text || "Missing storage environment"}</>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {isSelectStorage ? (
                    <DropDown
                      required={true}
                      placeholder="Select Facility Name"
                      upward={true}
                      currentData={facilities}
                      defaultValue={warehouse}
                      customGridColumn={"customGridColumnType"}
                      handleChangeState={(e: any, { value }: any) => {
                        setWarehouse(value);
                        setValue(`externalStorageData.${index}.facilityId`, value, {
                          shouldDirty: true,
                          shouldValidate: true
                        });
                      }}
                    />
                  ) : (
                    <>{data?.facilityId?.facilityName || "Missing facility name"}</>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {isSelectStorage ? (
                    <InputNumber
                      transform={{
                        input: (value: any) =>
                          isNaN(value) || value === 0 ? "" : value.toString(),
                        output: (e: any) => {
                          return e;
                        }
                      }}
                      control={control}
                      placeholder="Storage Temperature (℃)"
                      name={`externalStorageData.${index}.storageTemperature`}
                      enableMinus={true}
                      enableDecimalWithMinus
                    />
                  ) : (
                    <>{data?.storageTemperature}</>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {isSelectStorage ? (
                    <InputNumber
                      transform={{
                        input: (value: any) =>
                          isNaN(value) || value === 0 ? "" : value.toString(),
                        output: (e: any) => {
                          return e;
                        }
                      }}
                      control={control}
                      placeholder="Warehouse size (m3)"
                      name={`externalStorageData.${index}.warehouseSize`}
                      errorMessage="Warehouse size is required"
                    />
                  ) : (
                    <> {data?.warehouseSize}</>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {isSelectStorage ? (
                    <InputWithDropDown
                      defaultSelectValue={weightUnitValue}
                      options={WEIGHT_UNIT_TYPES_OPTIONs}
                      selectOption={(value: number) => setWeightUnit(value)}
                      control={control}
                      upward={true}
                      placeholder="Weight"
                      name={`externalStorageData.${index}.weight`}
                      defaultValue={0}
                      transform={{
                        input: (value: any) =>
                          isNaN(value) || value === 0 ? "" : value.toString(),
                        output: (e: any) => {
                          return e;
                        }
                      }}
                    />
                  ) : (
                    <>
                      {data?.weight} {weightUnit?.text}
                    </>
                  )}
                </Table.Cell>
                <Table.Cell className={checkValueIsEmpty(data?.electricity)}>
                  {isSelectStorage ? (
                    <InputNumber
                      control={control}
                      placeholder="Electricity"
                      name={`externalStorageData.${index}.electricity`}
                      transform={{
                        input: (v: any) =>
                          v === null || v === undefined || isNaN(v) ? "" : v.toString(),
                        output: (e: any) => e
                      }}
                      errors={(errors?.externalStorageData as any)?.[index]?.electricity}
                      required
                      errorMessage="Electricity is required"
                    />
                  ) : (
                    <>{data?.electricity || "Missing electricity"}</>
                  )}
                </Table.Cell>
                <Table.Cell className={checkValueIsEmpty(data?.warehouseUtilization)}>
                  {isSelectStorage ? (
                    <InputNumber
                      control={control}
                      placeholder="Utilization of Warehouse"
                      name={`externalStorageData.${index}.warehouseUtilization`}
                      transform={{
                        input: (v: any) =>
                          v === null || v === undefined || isNaN(v) ? "" : v.toString(),
                        output: (e: any) => e
                      }}
                      errors={(errors?.externalStorageData as any)?.[index]?.warehouseUtilization}
                      required
                      errorMessage="Utilization of Warehouse is required"
                    />
                  ) : (
                    <> {data?.warehouseUtilization || "Missing utilization of warehouse"}</>
                  )}
                </Table.Cell>
                <Table.Cell className={checkValueIsEmpty(data?.storageDays)}>
                  {isSelectStorage ? (
                    <InputNumber
                      control={control}
                      placeholder="Storage Days"
                      name={`externalStorageData.${index}.storageDays`}
                      transform={{
                        input: (v: any) =>
                          v === null || v === undefined || isNaN(v) ? "" : v.toString(),
                        output: (e: any) => e
                      }}
                      errors={(errors?.externalStorageData as any)?.[index]?.storageDays}
                      required
                      errorMessage="Storage Days is required"
                    />
                  ) : (
                    <>{data?.storageDays || "Missing storage days"}</>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Grid>
                    <Grid.Column computer={4} tablet={5} mobile={5}>
                      {isExternalStorageUpdateLoading && isSelectStorage ? (
                        <Loader active size="small" inline="centered" />
                      ) : isSelectStorage ? (
                        <MdSave
                          cursor="pointer"
                          size={24}
                          color="var(--tableEditIcon)"
                          title="Save"
                          onClick={handleSubmit(onSubmit)}
                        />
                      ) : (
                        <>
                          {isEditable && (
                            <MdModeEditOutline
                              cursor="pointer"
                              size={24}
                              color="var(--tableEditIcon)"
                              onClick={() => editData(data)}
                            />
                          )}
                        </>
                      )}
                      {/* )} */}
                    </Grid.Column>
                    {!isExternalStorageUpdateLoading && isSelectStorage ? (
                      <Grid.Column computer={4} tablet={16} mobile={16}>
                        <MdClose
                          cursor={"pointer"}
                          size={24}
                          color="var(--tableEditIcon)"
                          onClick={handleCancel}
                        />
                      </Grid.Column>
                    ) : null}
                  </Grid>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </>
  );
};

export default ExternalWarehouse;
